import { CheckIcon } from '@heroicons/react/16/solid';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  Children,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import InfinityList from '@/components/ui/custom/InfinityList';
import { Dropdown } from '@/components/ui/daisy/dropdown';
import { Loading } from '@/components/ui/daisy/loading';
import Fieldset from '@/components/ui/daisy/fieldset';
import { Menu } from '@/components/ui/daisy/menu';
import { TextInput } from '@/components/ui/daisy/textinput';

import {
  SearchSelectContextValue,
  SearchSelectOptionProps,
  SearchSelectProps,
  SearchSelectValue,
} from './types';
import { useSearchSelect } from './useSearchSelect';

const SearchSelectContext = createContext<SearchSelectContextValue>({
  value: undefined,
  onSelect: () => {},
  registerOption: () => {},
  unregisterOption: () => {},
});

const useSearchSelectContext = () => useContext(SearchSelectContext);

const SearchSelectBase = forwardRef<HTMLDivElement, SearchSelectProps>(
  (props, ref) => {
    const { t } = useTranslation('translation', { keyPrefix: 'searchSelect' });

    const {
      value,
      onChange,
      onClear,
      onSearch,
      onReachEnd,
      loading = false,
      hasNextPage,
      isFetchingNextPage,
      selectedLabel,
      placeholder = t('placeholder'),
      searchPlaceholder,
      emptyMessage = t('emptyMessage'),
      size = 'md',
      color = 'default',
      error = false,
      disabled = false,
      forceOpen = false,
      classNames,
      children,
    } = props;

    const [isFocused, setIsFocused] = useState(false);

    const {
      searchValue,
      selectedLabelText,
      handleSearchChange,
      handleSelect: baseHandleSelect,
      resetSearch,
      registerOption,
      unregisterOption,
    } = useSearchSelect({
      value,
      onChange,
      onSearch,
      disabled,
      selectedLabel,
    });

    const isOpen = isFocused || forceOpen;

    const handleSelect = useCallback(
      (optionValue: SearchSelectValue) => {
        baseHandleSelect(optionValue);
        setIsFocused(false);
      },
      [baseHandleSelect],
    );

    const handleFocus = () => setIsFocused(true);

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node)) return;
      setIsFocused(false);
      resetSearch();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        const htmlActiveElement = document.activeElement as HTMLElement;
        htmlActiveElement?.blur();
      }
    };

    const handleClear = () => {
      resetSearch();
      onClear?.();
    };

    const displayValue = isOpen ? searchValue : selectedLabelText;
    const displayPlaceholder = isOpen
      ? selectedLabelText || searchPlaceholder || placeholder
      : placeholder;

    const childCount = Children.toArray(children).length;

    const contextValue = useMemo<SearchSelectContextValue>(
      () => ({
        value,
        onSelect: handleSelect,
        registerOption,
        unregisterOption,
      }),
      [value, handleSelect, registerOption, unregisterOption],
    );

    return (
      <SearchSelectContext.Provider value={contextValue}>
        <Dropdown
          ref={ref}
          open={isOpen}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={twMerge('w-full', classNames?.base)}
        >
          <TextInput.Wrapper
            size={size}
            color={color}
            error={error}
            disabled={disabled}
            className="w-full"
          >
            <TextInput.Icon position="start">
              <MagnifyingGlassIcon className="size-3.5 opacity-50" />
            </TextInput.Icon>
            <TextInput
              type="search"
              placeholder={displayPlaceholder}
              value={displayValue}
              disabled={disabled}
              onChange={event => handleSearchChange(event.target.value)}
              onKeyDown={handleKeyDown}
              onClear={onClear && value ? handleClear : undefined}
            />
          </TextInput.Wrapper>

          <Dropdown.Content
            className={twMerge(
              'z-10 w-full! mt-2 shadow-sm',
              classNames?.listbox,
            )}
          >
            <div className="bg-base-100 rounded-box shadow-sm p-2 flex flex-col gap-2">
              <InfinityList
                role="listbox"
                className="menu p-0 max-h-60 flex-nowrap overflow-y-auto"
                onReachEnd={onReachEnd}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              >
                {children}
                <ConditionalRenderer condition={!loading && childCount === 0}>
                  <li className="p-2 text-base-content/50 text-center text-sm">
                    {emptyMessage}
                  </li>
                </ConditionalRenderer>
              </InfinityList>

              <ConditionalRenderer condition={loading && childCount === 0}>
                <div className="flex justify-center p-2">
                  <Loading color="primary" size="sm" />
                </div>
              </ConditionalRenderer>
            </div>
          </Dropdown.Content>
        </Dropdown>
      </SearchSelectContext.Provider>
    );
  },
);

const Option = ({
  value,
  children,
  disabled = false,
}: SearchSelectOptionProps) => {
  const {
    value: selectedValue,
    onSelect,
    registerOption,
    unregisterOption,
  } = useSearchSelectContext();

  const stringValue = String(value);
  const isSelected = String(selectedValue) === stringValue;

  const childrenRef = useRef(children);
  childrenRef.current = children;

  useEffect(() => {
    registerOption(stringValue, childrenRef.current);
    return () => unregisterOption(stringValue);
  }, [stringValue, registerOption, unregisterOption]);

  return (
    <Menu.Item
      role="option"
      aria-selected={isSelected}
      disabled={disabled}
      activeColor="neutral"
    >
      <button
        type="button"
        disabled={disabled}
        onMouseDown={event => event.preventDefault()}
        onClick={() => onSelect(value)}
        className="flex items-center"
      >
        <ConditionalRenderer
          condition={isSelected}
          fallback={<div className="size-4" />}
        >
          <CheckIcon className="size-4 text-base-content/50" />
        </ConditionalRenderer>

        {children}
      </button>
    </Menu.Item>
  );
};

SearchSelectBase.displayName = 'SearchSelect';
Option.displayName = 'SearchSelect.Option';

export const SearchSelect = Object.assign(SearchSelectBase, {
  Option,
  Fieldset,
});
